// The envelope slid under the door after a right call (sprint 28 item 3, roadmaps 63 and 64): odds, amounts and pure rules.
// Named "mail" in code because game/envelope.js is already the end-of-shift envelope (roadmap 29). Players only ever read "envelope".
// Pure and browser-safe, like game/seed.js: the odds page, /verify and the server share it. No weak random source anywhere.
// Points and cosmetics only. Never bought, never changed by holdings, wallet or payment.
// Spec: studio/sprints/28/item3-design.md.

// Each draw is a unit in [0, 1) from SHA-256("<shift seed>:<label>") (game/seed.js rollUnit / unitOfHex), one label per draw per round.
export const gildedLabel = roundId => 'gilded:' + roundId; // drawn for EVERY night-room knock, before the knock starts
export const dropLabel = roundId => 'drop:' + roundId;     // drawn on an eligible right call
export const tierLabel = roundId => 'tier:' + roundId;     // drawn on an eligible right call that dropped an envelope

// The published odds. Change a number here and the odds page, /verify and the tests follow.
export const MAIL = {
  gilded: 1 / 150,                 // a knock is secretly gilded. No tell. A right call on it opens the jackpot envelope.
  drop: 0.25,                      // an eligible right call is followed by an envelope (1 in 4)
  tiers: { points: 0.80, shield: 0.16, big: 0.04 }, // of the envelopes that drop; in this order, they sum to 1
  points: { points: 20, big: 150, jackpot: 500 },   // base points, times the lamp
  lamp: { step: 0.1, max: 2 },     // x1.1 per right call in a row, x2 at most; a wrong call or a timeout resets it
  fragments: { perWrongCall: 1, guarantee: 10 },
  cap: { rolls: 20, windowMs: 6 * 3600000 },
  coolDownMs: 3600000,             // after a big or a jackpot, a further big becomes a points envelope for 1 h (sprint 30: 12 h cancelled about half the bigs rolled)
  minDecideMs: 10000,
  shieldsHeld: 1,
};

export const gildedKnock = unit => unit < MAIL.gilded;
export const dropped = unit => unit < MAIL.drop;
export function tierOf(unit) {
  let edge = MAIL.tiers.points;
  if (unit < edge) return 'points';
  if (unit < Math.round((edge + MAIL.tiers.shield) * 1e9) / 1e9) return 'shield';
  return 'big';
}

// The lamp after n right calls in a row, the current one included: 1 -> x1.1 ... 10 -> x2. Whole tenths, so it never drifts.
export const lampOf = n => Math.min(MAIL.lamp.max, Math.round((1 + MAIL.lamp.step * Math.max(0, n)) * 10) / 10);

// Eligibility: the doorkeeper chatted (sent at least one line), the call took at least 10 s from the knock, they decided (not a timeout),
// the call was right, and the round is a night-room round with a human doorkeeper. Never on a wrong call, a timeout, the tutorial or the first minute.
// free: the free room's doors count too (sprint 30 item 3: the trial has no night shift, and nothing here needs a wallet).
export function eligible({ room, human = true, correct, verdict, chatted, decideMs, guided = false, free = false }) {
  return (room === 'night' || (free && room === 'practice')) && human && !guided && correct === true && (verdict === 'open' || verdict === 'shut') && !!chatted && decideMs >= MAIL.minDecideMs;
}
// A wrong call that counts toward the keyring: same conditions, but the call was wrong.
export const fragmentCall = r => eligible({ ...r, correct: true }) && r.correct === false;

// Server state per player: { rolls: [timestamps of counted rolls], coolUntil: ms, fragments: 0..10, shield: 0|1 }.
export const freshMail = () => ({ rolls: [], coolUntil: 0, fragments: 0, shield: 0 });
export const rollsLeft = (state, now) => Math.max(0, MAIL.cap.rolls - state.rolls.filter(t => now - t < MAIL.cap.windowMs).length);

// One eligible right call -> what happens. units: { gilded, drop, tier } from the seed. n: right calls in a row including this one.
// Returns the next state and the result; never mutates. The shift's gilded flag opens the jackpot and ignores the cap (it is not a roll);
// it still needs eligibility, so it only ever follows a right call.
export function openMail({ state, now, units, n, gilded = false }) {
  const lamp = lampOf(n);
  const s = { ...state, rolls: state.rolls.filter(t => now - t < MAIL.cap.windowMs) };
  if (gilded) {
    s.coolUntil = now + MAIL.coolDownMs;
    return { state: s, mail: { tier: 'jackpot', rolled: 'jackpot', points: Math.round(MAIL.points.jackpot * lamp), lamp, cosmetic: 'gilded' } };
  }
  if (s.rolls.length >= MAIL.cap.rolls) return { state: s, mail: null, why: 'cap' };
  s.rolls = [...s.rolls, now];
  const guaranteed = s.fragments >= MAIL.fragments.guarantee;
  if (!guaranteed && !dropped(units.drop)) return { state: s, mail: null, why: 'none' };
  if (guaranteed) s.fragments = 0;
  const rolled = tierOf(units.tier);
  let tier = rolled, note;
  if (rolled === 'big' && now < s.coolUntil) { tier = 'points'; note = 'cooldown'; }
  if (rolled === 'shield' && s.shield >= MAIL.shieldsHeld) { tier = 'points'; note = 'shield-held'; }
  if (tier === 'big') s.coolUntil = now + MAIL.coolDownMs;
  if (tier === 'shield') s.shield += 1;
  const points = tier === 'shield' ? 0 : Math.round(MAIL.points[tier] * lamp);
  return { state: s, mail: { tier, rolled, points, lamp, guaranteed, ...(note ? { note } : {}), ...(tier === 'big' ? { cosmetic: 'big' } : {}) } };
}

// A wrong call (eligible by the fragment rule) adds a fragment; an unspent shield absorbs the reset. Returns { state, keptStreak }.
export function wrongCall({ state, counts }) {
  const s = { ...state };
  if (counts) s.fragments = Math.min(MAIL.fragments.guarantee, s.fragments + MAIL.fragments.perWrongCall);
  const keptStreak = s.shield > 0;
  if (keptStreak) s.shield -= 1;
  return { state: s, keptStreak };
}

// The odds page. Points and cosmetics only: nothing here names money, prizes or winnings. Tested against the forbidden words.
export const pct = x => `${Math.round(x * 10000) / 100}%`.replace('.0%', '%');
export const ODDS_PAGE = {
  title: 'Envelope odds',
  lines: [
    'After a right call, an envelope might slide under the door. It holds points, a streak shield or a cosmetic mark, and nothing else.',
    `Chance of an envelope after an eligible right call: 1 in ${Math.round(1 / MAIL.drop)}.`,
    `Inside: points ${pct(MAIL.tiers.points)}, streak shield ${pct(MAIL.tiers.shield)}, big ${pct(MAIL.tiers.big)}.`,
    `A points envelope is worth ${MAIL.points.points} and a big one ${MAIL.points.big}, both multiplied by your streak lamp.`,
    `The lamp goes up x${1 + MAIL.lamp.step} for each right call in a row, up to x${MAIL.lamp.max}. A wrong call or a timeout puts it out.`,
    `About 1 in ${Math.round(1 / MAIL.gilded)} knocks is gilded, and you can't tell which. A right call on one opens a gilded envelope worth ${MAIL.points.jackpot} points times the lamp, plus a gilded mark.`,
    `A streak shield saves your streak from one wrong call. You can only hold one at a time.`,
    `Each wrong call drops a keyring fragment, as long as you chatted and waited 10 seconds. Collect ${MAIL.fragments.guarantee} fragments and the next envelope is guaranteed.`,
    `You get at most ${MAIL.cap.rolls} envelope rolls every 6 hours. After a big or gilded envelope, there's no other big one for ${MAIL.coolDownMs / 3600000} hour.`,
    'You roll only if you chatted, took at least 10 seconds and made the call. A wrong call never rolls.',
    'Envelopes work the same in the practice room and on the night shift, and the practice room needs no sign-in. They give points and cosmetic marks only, and nothing else.',
    "Every roll comes from the shift seed and the round, and is shown on the verify page after the shift. Holding KNOCK doesn't change anything. You don't get an extra roll, a better chance or a bigger envelope. Paying more doesn't change anything either, and you can't buy envelopes.",
  ],
};
// The share-card line for the punchline bank already exists (client/punchlines.js, key "envelope"). Reveal words for the peephole:
export const REVEAL = {
  points: n => `${n} points`,
  shield: 'A streak shield',
  big: n => `A big one. ${n} points.`,
  jackpot: n => `Gilded. ${n} points.`,
  shieldHeld: n => `You already have a shield, so here are ${n} points instead.`,
  cooldown: n => `${n} points.`,
  capped: 'No more envelopes for now.',
};

// The words on the peephole's result for what openMail returned (the held shield and the cool-down show their points only).
export function mailText(mail) {
  if (!mail) return '';
  if (mail.tier === 'shield') return REVEAL.shield;
  if (mail.tier === 'jackpot') return REVEAL.jackpot(mail.points);
  if (mail.note === 'shield-held') return REVEAL.shieldHeld(mail.points);
  if (mail.note === 'cooldown') return REVEAL.cooldown(mail.points);
  return mail.tier === 'big' ? REVEAL.big(mail.points) : REVEAL.points(mail.points);
}
